import {
  Ban,
  ChevronDown,
  ChevronRight,
  Globe,
  Layers,
  MapPin,
  Rocket,
  Shield,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react';
import {
  Fragment,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import type { Network } from '../../api/types';
import { strings } from '../../strings';
import { attachmentPresentation, NetworkIcon, providerLabel } from '../shared';
import {
  cardStateWords,
  requestWords,
  topologyShape,
  treeKey,
  waitsForNetworkApply,
  type TopologyItem,
  type TopologyModel,
} from './topology';

const p = strings.networks.page;
const tp = strings.networks.topology;
const ep = strings.networks.entryPoints;

interface RouteLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Route lines (phase 3, dashed, `aria-hidden`): only from the selected entry point to its origins' attachment cards, or
 * from the selected attachment to its entry points. Measured from the rendered cards, redrawn on resize.
 */
function useRouteLines(
  container: { current: HTMLElement | null },
  pairs: readonly [string, string][],
): RouteLine[] {
  const [lines, setLines] = useState<RouteLine[]>([]);
  const key = pairs.map((x) => x.join('>')).join('|');
  useLayoutEffect(() => {
    const root = container.current;
    if (!root || !pairs.length) return;
    const measure = () => {
      const box = root.getBoundingClientRect();
      const at = (id: string) =>
        root
          .querySelector<HTMLElement>(`[data-tree-id="${id.replace(/"/g, '\\"')}"]`)
          ?.getBoundingClientRect();
      const next: RouteLine[] = [];
      for (const [from, to] of pairs) {
        const a = at(from);
        const b = at(to);
        if (!a || !b) continue;
        next.push({
          x1: a.left + a.width / 2 - box.left,
          y1: a.bottom - box.top,
          x2: b.left + b.width / 2 - box.left,
          y2: b.top - box.top,
        });
      }
      setLines(next);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, key]);
  return pairs.length ? lines : [];
}

/** A state dot (an image with the state as its name is not needed: the words follow it). */
function Dot({ tone }: { tone: string }) {
  return <span aria-hidden className={`net-dot net-dot-${tone}`} />;
}

function focusTreeItem(treeId: string, id: string) {
  document
    .getElementById(treeId)
    ?.querySelector<HTMLElement>(`[data-tree-id="${id.replace(/"/g, '\\"')}"]`)
    ?.focus();
}

/**
 * The topology diagram as one `role="tree"` (spec 11 6.15.5): HTML nodes in provider columns with region cards and
 * attachment cards; the Entry points band above and the Private DNS band below. One item is in the Tab order (roving);
 * Up/Down, Right, Left, Home, End and Enter follow the APG tree view; Enter selects (`?select=`), Enter on the
 * selected item moves focus into the side panel. `isolated` draws no lines.
 */
export function TopologyTree({
  network,
  model,
  selected,
  dnsLine,
  onSelect,
  onOpenGroup,
  onEnterPanel,
  focusRef,
}: {
  network: Network;
  model: TopologyModel;
  selected: string | null;
  dnsLine: string;
  onSelect: (id: string) => void;
  onOpenGroup: (regionId: string, firstId: string | null) => void;
  onEnterPanel: () => void;
  /** Lets the page return focus to an item (Esc in the side panel). */
  focusRef: { current: ((id: string) => void) | null };
}) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [focused, setFocused] = useState<string | null>(null);
  const treeId = useId();
  const firstVisible = model.roots[0] ?? null;
  const tabStop =
    focused && model.items.has(focused)
      ? focused
      : selected && model.items.has(selected)
        ? selected
        : firstVisible;

  const focusItem = (id: string) => {
    setFocused(id);
    requestAnimationFrame(() => focusTreeItem(treeId, id));
  };
  useEffect(() => {
    focusRef.current = (id: string) => {
      setFocused(id);
      requestAnimationFrame(() => focusTreeItem(treeId, id));
    };
  }, [focusRef, treeId]);

  const toggle = (id: string, open: boolean) =>
    setCollapsed((c) => {
      const next = new Set(c);
      if (open) next.delete(id);
      else next.add(id);
      return next;
    });

  const activate = (item: TopologyItem) => {
    if (item.kind === 'group') {
      onOpenGroup(
        item.parentId!,
        item.grouped?.[0]
          ? `network.attachment/${item.grouped[0].deploymentId}/${item.grouped[0].provider}/${item.grouped[0].region}`
          : null,
      );
      return;
    }
    if (item.selectable) {
      if (selected === item.id) onEnterPanel();
      else onSelect(item.id);
    } else if (item.expandable) toggle(item.id, collapsed.has(item.id));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>, item: TopologyItem) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      activate(item);
      return;
    }
    const action = treeKey(model, collapsed, item.id, event.key);
    if (!action) return;
    event.preventDefault();
    event.stopPropagation();
    if (action.type === 'focus') focusItem(action.id);
    else if (action.type === 'expand') toggle(action.id, true);
    else if (action.type === 'collapse') toggle(action.id, false);
  };

  const itemProps = (item: TopologyItem, className: string) => ({
    role: 'treeitem' as const,
    'aria-level': item.level,
    'aria-label': item.name,
    'aria-selected': item.selectable ? selected === item.id : undefined,
    'aria-expanded': item.expandable ? !collapsed.has(item.id) : undefined,
    'data-tree-id': item.id,
    tabIndex: tabStop === item.id ? 0 : -1,
    className: `${className} ${selected === item.id ? 'is-selected' : ''}`,
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => onKeyDown(event, item),
    onFocus: (event: { target: EventTarget; currentTarget: EventTarget }) => {
      if (event.target === event.currentTarget) setFocused(item.id);
    },
    onClick: (event: { stopPropagation: () => void }) => {
      event.stopPropagation();
      setFocused(item.id);
      activate(item);
    },
  });

  const chevron = (item: TopologyItem): ReactNode =>
    item.expandable ? (
      <span
        aria-hidden
        className="net-chevron"
        onClick={(event) => {
          event.stopPropagation();
          toggle(item.id, collapsed.has(item.id));
        }}
      >
        {collapsed.has(item.id) ? <ChevronRight className="size-4" /> : <ChevronDown className="size-4" />}
      </span>
    ) : null;

  // Route lines and targets (phase 3): the selected entry point's origins, or the selected attachment's entry points.
  const selectedItem = selected ? model.items.get(selected) : undefined;
  const entryItems = [...model.items.values()].filter((i) => i.kind === 'entry-point');
  const attachmentItems = [...model.items.values()].filter((i) => i.kind === 'attachment');
  const pairs: [string, string][] =
    selectedItem?.kind === 'entry-point'
      ? attachmentItems
          .filter((a) => selectedItem.entryPoint!.origins.includes(a.attachment!.deploymentId))
          .map((a) => [selectedItem.id, a.id])
      : selectedItem?.kind === 'attachment'
        ? entryItems
            .filter((x) => x.entryPoint!.origins.includes(selectedItem.attachment!.deploymentId))
            .map((x) => [x.id, selectedItem.id])
        : [];
  const targets = new Set(pairs.flatMap(([a, b]) => [a, b]));
  const diagram = useRef<HTMLDivElement>(null);
  const lines = useRouteLines(diagram, pairs);

  const renderEntryPoint = (item: TopologyItem) => {
    const x = item.entryPoint!;
    return (
      <div
        key={item.id}
        {...itemProps(item, `net-ep-mini ${targets.has(item.id) ? 'is-route-target' : ''}`)}
        data-testid={`band-entry-point-${x.id}`}
      >
        <span aria-hidden className="net-edge-abbr">
          {x.abbr}
        </span>
        <span className="font-semibold">{x.id}</span>
        <span className="text-fg-muted">
          {[x.kind, x.tier, x.region ? ep.regionHub(x.region) : null].filter(Boolean).join(' · ')}
        </span>
        {x.waf && (
          <span className={`badge ${x.waf === 'detection' ? 'tone-warning' : 'tone-success'}`}>
            {ep.wafWords(x.waf)}
          </span>
        )}
        {x.pending > 0 && (
          <span className="badge tone-warning" title={ep.pendingCount(x.pending)}>
            <TriangleAlert aria-hidden className="size-3.5" />
            {x.pending}
          </span>
        )}
      </div>
    );
  };

  const renderAttachment = (item: TopologyItem) => {
    const a = item.attachment!;
    const presentation = attachmentPresentation(a.state, waitsForNetworkApply(a, network));
    const shape = topologyShape(network, a.provider);
    const requests = requestWords(a);
    return (
      <div key={item.id} {...itemProps(item, `net-card ${targets.has(item.id) ? 'is-route-target' : ''}`)}>
        <span className="flex items-center gap-1.5 font-mono text-[13px] font-semibold">
          <Rocket aria-hidden className="size-3.5 shrink-0 text-fg-muted" />
          <span className="truncate">{a.deploymentName}</span>
          {shape.requestsOnly && (
            <ShieldCheck aria-hidden className="net-card-shield ml-auto size-3.5 shrink-0" />
          )}
        </span>
        <span className="block font-mono text-[12px] text-fg-muted">{a.cidr}</span>
        <span className="mt-1 flex items-center gap-1.5 text-[12px]">
          <Dot tone={presentation.tone} />
          {cardStateWords(a, network)}
        </span>
        {requests && <span className="block text-[11.5px] text-fg-muted">{requests}</span>}
        {!!item.entryRoutes && !targets.size && (
          <span className="badge tone-info mt-1 w-fit" data-testid="entry-routes-badge">
            {ep.entryRoutes(item.entryRoutes)}
          </span>
        )}
      </div>
    );
  };

  const renderHub = (item: TopologyItem) => {
    const hub = item.hub!;
    return (
      <div key={item.id} {...itemProps(item, 'net-hub')} data-testid="hub-card">
        <span className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
          <span className="font-semibold">{tp.hub}</span>
          <span className="font-mono text-[12px]">{hub.cidr}</span>
        </span>
        <span className="mt-0.5 flex items-center gap-1.5 text-[12px]">
          {hub.firewall ? (
            <>
              <Shield aria-hidden className="size-3.5 shrink-0" />
              {hub.firewall}
            </>
          ) : (
            <span className="text-fg-muted">{tp.underneathDirect}</span>
          )}
        </span>
        {hub.blocked && (
          <span className="mt-1 inline-flex items-center gap-1 text-[12px] font-semibold">
            <Ban aria-hidden className="size-3.5" />
            {tp.noSpokeToSpoke}
          </span>
        )}
      </div>
    );
  };

  const renderGroup = (item: TopologyItem) => (
    <div key={item.id} {...itemProps(item, 'net-card net-card-group')}>
      <span className="flex items-center gap-1.5 text-[13px] font-semibold">
        <Layers aria-hidden className="size-3.5 shrink-0 text-fg-muted" />
        {p.more(item.grouped!.length, item.summary ?? '')}
      </span>
    </div>
  );

  const renderRegion = (item: TopologyItem) => {
    const blocks = (item.blocks ?? []).join(', ');
    const children = item.children.map((id) => model.items.get(id)!);
    const hub = children.find((c) => c.kind === 'hub');
    const cards = children.filter((c) => c.kind !== 'hub');
    const shape = topologyShape(network, item.provider!);
    const open = !collapsed.has(item.id);
    return (
      <div key={item.id} {...itemProps(item, 'net-region')}>
        <div className="net-region-head">
          {chevron(item)}
          <MapPin aria-hidden className="size-4 shrink-0 text-fg-muted" />
          <span className="font-semibold">{item.region}</span>
          <span className="font-mono text-[12px] text-fg-muted">{blocks}</span>
        </div>
        {children.length === 0 ? (
          <p className="px-2 pb-2 text-[12px] text-fg-muted">{p.noAttachmentsInRegion}</p>
        ) : (
          open && (
            <div role="group" className={`net-region-body ${shape.mesh ? 'is-mesh' : ''}`}>
              {hub && renderHub(hub)}
              {shape.mesh && cards.length > 0 && (
                <span aria-hidden className="net-mesh-label">
                  {tp.meshEnclosure}
                </span>
              )}
              {cards.length === 0 ? (
                <p className="px-2 pb-2 text-[12px] text-fg-muted">{p.noAttachmentsInRegion}</p>
              ) : (
                <div
                  role="none"
                  className={`net-cards ${hub ? 'has-trunk' : ''} ${cards.length === 1 ? 'is-single' : ''}`}
                >
                  {cards.map((child) =>
                    child.kind === 'group' ? renderGroup(child) : renderAttachment(child),
                  )}
                </div>
              )}
            </div>
          )
        )}
      </div>
    );
  };

  const renderProvider = (item: TopologyItem) => {
    const regions = item.children.map((id) => model.items.get(id)!);
    return (
      <div key={item.id} {...itemProps(item, 'net-provider')}>
        <div className="net-provider-head">
          {chevron(item)}
          <span className="net-provider-tile">{providerLabel(item.provider!).slice(0, 2).toUpperCase()}</span>
          <span className="text-[11.5px] font-semibold tracking-wide uppercase">
            {providerLabel(item.provider!)}
          </span>
        </div>
        {!collapsed.has(item.id) && (
          <div role="group" className="net-regions">
            {regions.map((region, i) => (
              <Fragment key={region.id}>
                {i > 0 && topologyShape(network, item.provider!).crossRegion && (
                  <span aria-hidden className="net-region-link">
                    {tp.connectedRegions}
                  </span>
                )}
                {renderRegion(region)}
              </Fragment>
            ))}
          </div>
        )}
      </div>
    );
  };

  const roots = model.roots.map((id) => model.items.get(id)!);
  const entry = roots.find((r) => r.kind === 'entry-points');
  const dns = roots.find((r) => r.kind === 'dns');
  const providers = roots.filter((r) => r.kind === 'provider');

  return (
    <div
      id={treeId}
      ref={diagram}
      role="tree"
      aria-label={p.treeLabel(network.name)}
      className="net-diagram"
      data-testid="topology-tree"
    >
      {lines.length > 0 && (
        <svg aria-hidden className="net-route-lines" data-testid="route-lines">
          {lines.map((l, i) => (
            <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} />
          ))}
        </svg>
      )}
      {entry && (
        <div {...itemProps(entry, 'net-band')}>
          <span className="net-band-label">{p.entryPoints}</span>
          {entry.children.length === 0 ? (
            <span className="flex items-center gap-1.5 text-[13px] text-fg-muted">
              <Globe aria-hidden className="size-4" />
              {p.noEntryPoints}
            </span>
          ) : (
            !collapsed.has(entry.id) && (
              <div role="group" className="net-ep-band">
                {entry.children.map((id) => renderEntryPoint(model.items.get(id)!))}
              </div>
            )
          )}
        </div>
      )}
      <div role="none" className="net-providers">
        {providers.map(renderProvider)}
      </div>
      {dns && (
        <div {...itemProps(dns, 'net-band')}>
          <span className="net-band-label">{p.privateDns}</span>
          <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px]">
            <NetworkIcon aria-hidden className="size-4 shrink-0 text-fg-muted" />
            {dnsLine}
          </span>
          <span className="text-[12px] text-fg-muted">{p.writtenByNetwork}</span>
        </div>
      )}
    </div>
  );
}
